// Showing what a device record actually holds (issue #737).
//
// The Product Data panel was driven by a hardcoded list of ~33 field names,
// written for the Wave II and Performance series. Those products were ingested
// into a known shape, so a fixed list worked.
//
// Wave XL is not that shape. A WE650 record carries around 150 populated fields
// -- component part numbers with MAC addresses and LOT codes, burn-in start and
// end times, torque figures, twelve-position alignment measurements, and a
// pass/fail matrix covering every video input, touch, pixel defect and cosmetic
// check. Three of them were on screen. Everything else was in the database and
// invisible.
//
// So the panel is built from the record instead of from a list. New products
// bring new fields and they show up on their own.

// Never rendered. Two different reasons, kept apart deliberately.
const SECRET_KEYS = new Set([
  // Holds tokenHash -- the credential for the device's public share link.
  'publicAccess',
]);

const INTERNAL_KEYS = new Set([
  '_id', '__v', 'id', 'ID',
  // Rendered by their own dedicated panels elsewhere on the page.
  'attributes', 'productionLogs', 'gpvRmaFollowup', 'failureData', 'progress',
  'decommissioned', 'companyProfile', 'manufacturerProfile',
  // Bookkeeping the page shows in its own header, or that means nothing here.
  'serialNumber', 'status', 'received', 'assigned', 'buildOrderId',
  'failureCount', 'operatingHours', 'createdAt', 'updatedAt', '_ingestedAtUtc',
]);

export const HIDDEN_KEYS = new Set([...SECRET_KEYS, ...INTERNAL_KEYS]);

/** A field name a person can read: "Scaler_BoardPN" -> "Scaler Board PN". */
export function humanizeKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    // Split camelCase and PascalCase runs, but keep acronyms together:
    // "BoardPN" -> "Board PN", not "Board P N".
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, c => c.toUpperCase());
}

const isEmpty = value =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

// A dash is how the check sheet writes "not applicable". Showing a screen full
// of dashes is worse than showing nothing.
const isPlaceholder = value => typeof value === 'string' && ['-', '--', 'n/a', 'na'].includes(value.trim().toLowerCase());

const shouldShow = value => !isEmpty(value) && !isPlaceholder(value) && typeof value !== 'object';

/**
 * The commissioning check sheet stores each test as three keys:
 * `X_Spec`, `X`, `X_Remark`. Rendered as separate rows that is 75 lines of
 * near-duplicate text; folded together it is a readable results table.
 */
export function foldTestResults(device) {
  const results = [];
  const consumed = new Set();

  Object.keys(device || {}).forEach(key => {
    if (!key.endsWith('_Spec')) return;
    const base = key.slice(0, -'_Spec'.length);
    if (!(base in device)) return;

    const spec = device[`${base}_Spec`];
    const result = device[base];
    const remark = device[`${base}_Remark`];

    consumed.add(`${base}_Spec`);
    consumed.add(base);
    if (`${base}_Remark` in device) consumed.add(`${base}_Remark`);

    if (isEmpty(result)) return;
    results.push({
      key: base,
      label: humanizeKey(base),
      spec: isPlaceholder(spec) || isEmpty(spec) ? '' : String(spec),
      result: String(result),
      remark: isPlaceholder(remark) || isEmpty(remark) ? '' : String(remark),
    });
  });

  return { results, consumed };
}

/**
 * Everything worth showing about a device, in three parts:
 *
 *   curated  — the named fields the panel has always shown, in their order
 *   tests    — the folded pass/fail matrix, if the record has one
 *   other    — every remaining populated field, named from its own key
 *
 * `curatedLabels` is the existing label map, so known products render exactly
 * as they did before and only the previously invisible fields are new.
 */
export function buildDeviceFields(device, curatedLabels = {}) {
  if (!device) return { curated: [], tests: [], other: [] };

  const curated = Object.entries(curatedLabels)
    .filter(([key]) => shouldShow(device[key]))
    .map(([key, label]) => ({ key, label, value: String(device[key]) }));

  const { results, consumed } = foldTestResults(device);

  const other = Object.keys(device)
    .filter(key => !HIDDEN_KEYS.has(key))
    .filter(key => !(key in curatedLabels))
    .filter(key => !consumed.has(key))
    .filter(key => shouldShow(device[key]))
    .map(key => ({ key, label: humanizeKey(key), value: String(device[key]) }));

  return { curated, tests: results, other };
}
